import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { isAdmin } from "auth/tier";
import { currentUserIdOf } from "auth/userId";
import {
  sortCapabilities,
  summarizeCapabilities,
  type CapabilityReadiness,
  type CapabilityReadinessSummary,
  type CapabilityRecord,
} from "views/admin/SystemHealth/capabilityReadiness";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import type {
  DashboardSection,
  DashboardSummary,
  DashboardDevopsEventRow,
} from "../home.types";
import {
  subscribeHomeDevopsEvents,
  buildDevOpsEventTitle,
  type HomeDevopsEnvelope,
} from "../homeLive";
import {
  deriveAttentionSignals,
  type AttentionSignal,
} from "./CommandCenter.attention";

import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import { swallowedSessionEnded } from "shared/loadOrError";
/** Auto-refresh cadence while the route is mounted. */
export const DASHBOARD_REFRESH_MS = 60_000;

/**
 * How many rows the activity feed shows.
 *
 * This mirrors a SERVER cap: `dashboard-summary` selects the newest 10
 * `devops_events` rows (dashboard-summary.class.ts `findDevopsEvents`), so
 * raising this number here would not fetch more. The sibling `last24h` field is
 * a real COUNT over the last 24 hours and is NOT capped — the two numbers
 * measure different things, which is exactly why the widget has to say so.
 */
export const DASHBOARD_RECENT_EVENT_LIMIT = 10;

/**
 * View store for the Command Center landing dashboard (route 'Home').
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. Both services this store reads are find-only custom
 * services whose `find()` returns ONE composite object (no `{ data }` envelope,
 * no entity list — see home.types.ts), so this store reads the `find()` RESULT
 * directly off the registered ServiceStore rather than its entity cache, and
 * the composite objects live here.
 *
 * TWO INDEPENDENT LOADS, ONE CADENCE:
 *
 *  - `dashboard-summary` — the whole-platform rollup, readable by every staff
 *    tier. This is the dashboard.
 *  - `capability-readiness` — the admin-only "could this actually succeed right
 *    now?" contract. Non-admins NEVER call it (the API 403s), and the page must
 *    render without a hole for them: the readiness card and the readiness
 *    attention row are simply absent, not empty.
 *
 * They refresh together on the same 60s interval but run in PARALLEL and fail
 * INDEPENDENTLY. A readiness outage must never blank the dashboard, and a
 * dashboard outage must never hide a critical capability finding — the two most
 * plausible ways this screen could lie to an operator.
 *
 * DEGRADATION: a failed fetch keeps the last good value on both sides (the Body
 * shows a stale-data warning); with no summary at all every card renders "—".
 */
class CommandCenterStore {
  rootStore: RootStore;

  /** Last successfully fetched composite rollup (kept on refresh failure). */
  summary: DashboardSummary | null = null;
  /**
   * Tickets waiting on me — null until the first read. A failed read keeps
   * the last value but raises `ticketsCheckFailed`, and the band then shows
   * the failure instead of a number it cannot vouch for.
   */
  ticketsWaitingOnMe: number | null = null;
  loading = false;
  error: string | null = null;
  lastUpdatedAt: Date | null = null;
  /** Live devops rows received since the last successful poll (newest first). */
  liveEvents: DashboardDevopsEventRow[] = [];

  /** Last successfully fetched readiness report (admin only; else always null). */
  readiness: CapabilityReadiness | null = null;
  readinessLoading = false;
  readinessError: string | null = null;
  /** True once a readiness load has SETTLED (succeeded or failed) at least once. */
  readinessSettled = false;

  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private unsubscribeLive: (() => void) | null = null;
  /** Monotonic guard so a stale fetch can never clobber a newer one. */
  private loadSeq = 0;
  /** The same guard for the independent readiness fetch. */
  private readinessSeq = 0;
  /** Synthetic-id counter for live rows (no DB id until persisted). */
  private liveSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /**
   * The registered `dashboard-summary` ServiceStore (see
   * serviceStores/home.stores.ts). find-only: its `find()` resolves the whole
   * composite rollup object, which `load()` stores directly (the entity cache
   * stays empty — there is no list of rows to key).
   */
  private get summaryService(): ServiceStoreType | null {
    return (this.rootStore.dashboardSummaryStore as ServiceStoreType | null) ?? null;
  }

  /**
   * The registered `capability-readiness` ServiceStore (serviceStores/
   * system.stores.ts). Resolved through the manager with the flat
   * `<name>Store` property as the fallback — the same lookup every other view
   * store in this app uses — and guarded for null, because this store must keep
   * working in an app build where the service store was never registered.
   */
  private get readinessService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("capability-readiness") ??
        this.rootStore.capabilityReadinessStore) as unknown as ServiceStoreType | null) ??
      null
    );
  }

  // ---- Lifecycle ------------------------------------------------------------

  /**
   * Route entry point (idempotent — the header Refresh button re-runs
   * `onEnter`). Ensures the realtime subscription + the 60s polling interval
   * are live, then fetches everything.
   */
  async initialize() {
    if (!this.unsubscribeLive) {
      // Shared realtime bus (src/realtime): ONE connection for the whole app,
      // refcounted there, JWT sourced internally from the AuthStore. This call
      // previously passed NO token, which made the live feed inert unless some
      // other module happened to have built the shared socket first.
      this.unsubscribeLive = subscribeHomeDevopsEvents(
        this.rootStore,
        (envelope) => this.handleLiveEvent(envelope)
      );
    }
    if (!this.refreshTimer) {
      this.refreshTimer = setInterval(() => {
        void this.refresh();
      }, DASHBOARD_REFRESH_MS);
    }
    await this.refresh();
  }

  /**
   * One poll: both reads, in parallel, each already swallowing its own failure
   * into its own error field. `Promise.all` is safe here precisely BECAUSE
   * neither `load()` nor `loadReadiness()` rejects — if either ever starts to,
   * the other's result would be discarded, which is the failure mode this
   * separation exists to prevent.
   */
  async refresh() {
    await Promise.all([this.load(), this.loadReadiness(), this.loadTickets()]);
  }

  /**
   * The count behind the "tickets waiting on you" row. One cheap read of the
   * `tickets` service's `mine` scope; the API's `total` is the number. Never
   * rejects, never touches `summary` or `error` — same contract as the others.
   */
  /** False until loadTickets has an answer — including "the service is absent". */
  ticketsSettled = false;
  /**
   * The last count attempt FAILED (swallowed by the ServiceStore, or thrown).
   * Still settled — the page must not stay blank — but the band renders the
   * `tickets-unchecked` warning, never "All clear", while nobody knows how
   * many tickets wait on this person. Cleared by the next successful count.
   */
  ticketsCheckFailed = false;
  /**
   * Whose count this is: the signed-in id it was taken for, `null` when it was
   * taken with nobody signed in, and `undefined` while no attempt has finished
   * at all. The three states have to be distinct — `null` alone could not tell
   * "counted for nobody" from "never counted", and the derivation below has to
   * know which.
   */
  private ticketsCountedFor: number | null | undefined = undefined;

  /**
   * Is the settled ticket state THIS person's? `ticketsSettled` only says an
   * attempt finished; it says nothing about who it finished for. Between an
   * account switch and the new person's first `loadTickets`, the count still
   * holds the previous account's number.
   *
   * A method, not a getter, for the same reason as `currentUserId` above: a
   * computed over the auth store would cache the first person it ever saw.
   */
  private ticketsAreCurrentUsers(): boolean {
    return (
      this.ticketsCountedFor !== undefined &&
      this.ticketsCountedFor === this.currentUserId()
    );
  }

  /**
   * A method, not a getter: a computed over the auth store would go stale.
   *
   * WHAT COUNTS AS A PERSON comes from `auth/userId`, not from a `typeof raw
   * === "number"` written here. On a cold restore the auth library hands back
   * the id as the STRING "5" until the first server round-trip, and read as
   * "nobody is signed in" that window collapses every account into one: the
   * guard below compares null to null, finds them equal, and publishes A's "3
   * tickets are waiting on you" as B's.
   */
  private currentUserId(): number | null {
    return currentUserIdOf(this.rootStore);
  }

  async loadTickets() {
    // A failed read leaves the count untouched, so without this an account
    // switch could keep showing the previous person's "3 tickets are waiting
    // on you" indefinitely.
    const who = this.currentUserId();
    if (this.ticketsCountedFor !== undefined && this.ticketsCountedFor !== who) {
      runInAction(() => {
        this.ticketsWaitingOnMe = null;
        this.ticketsSettled = false;
        this.ticketsCheckFailed = false;
        this.ticketsCountedFor = undefined;
      });
    }
    const ended = sessionMark(this.rootStore);
    const service = (this.rootStore.ticketsStore as ServiceStoreType | null) ?? null;
    if (!service) {
      // "There is no ticket service" IS this person's answer — recorded as
      // such so the band can settle instead of waiting forever for a count
      // nobody will ever take.
      runInAction(() => {
        this.ticketsSettled = true;
        this.ticketsCountedFor = who;
      });
      return;
    }
    try {
      const result = (await service.find({ query: { scope: "mine", $limit: 1 } })) as
        | unknown as { total?: unknown } | unknown[] | null | undefined;
      const total = Array.isArray(result) ? result.length : typeof result?.total === "number" ? result.total : null;
      // Current-user-wins, not last-response-wins: a slow read started before
      // an account switch must not land as the new person's count — nor its
      // failure as the new person's failure.
      if (this.currentUserId() !== who) return;
      if (total === null) {
        // A sign-out ended the read: no verdict for the next person's band.
        if (swallowedSessionEnded(service) || ended()) return;
        // The ServiceStore swallowed a failure and resolved null: not a count
        // of zero, a count that could not be taken.
        runInAction(() => {
          this.ticketsCheckFailed = true;
          this.ticketsCountedFor = who;
        });
        // Say WHY, like every other swallowed-read site in this store: the
        // ServiceStore records the transport failure on `errorOnFind` and
        // resolves null, and a warning without it is unactionable.
        const findError = (service as { errorOnFind?: unknown } | null)
          ?.errorOnFind;
        console.warn(
          "tickets count failed (swallowed by ServiceStore):",
          findError instanceof Error ? findError.message : findError
        );
        return;
      }
      runInAction(() => {
        this.ticketsWaitingOnMe = total;
        this.ticketsCheckFailed = false;
        this.ticketsCountedFor = who;
      });
    } catch (err) {
      if (isSessionEnded(err) || ended()) return;
      if (this.currentUserId() === who) {
        runInAction(() => {
          this.ticketsCheckFailed = true;
          this.ticketsCountedFor = who;
        });
      }
      console.warn("tickets count failed:", err);
    } finally {
      // Settled either way, once the attempt is over. A failed count is not
      // shown by omitting the row — that rendered as "All clear" — but by the
      // `tickets-unchecked` warning the derivation emits from the flag above.
      // Not after a sign-out: reset() un-settled the band for the next person.
      if (!ended()) {
        runInAction(() => {
          this.ticketsSettled = true;
        });
      }
    }
  }

  /** Route exit: tear down the interval and the realtime subscription. */
  dispose() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.unsubscribeLive?.();
    this.unsubscribeLive = null;
  }

  // ---- Derived data ---------------------------------------------------------

  /**
   * The activity feed to render: live rows first (newest events since the last
   * poll), then the server's persisted recent rows, capped at 10. Live rows
   * are cleared on every successful poll, so duplicates only exist for the
   * sub-minute window between an event firing and the next refresh.
   */
  get recentDevopsEvents(): DashboardDevopsEventRow[] {
    const server = this.summary?.devopsEvents?.recent ?? [];
    return [...this.liveEvents, ...server].slice(0, DASHBOARD_RECENT_EVENT_LIMIT);
  }

  /**
   * True when the feed is showing a full page — i.e. the list is a window on
   * the event stream, not the stream. The widget captions this so the
   * uncapped "in last 24h" COUNT beside it cannot be read as the length of
   * the list below it.
   */
  get recentEventsCapped(): boolean {
    return this.recentDevopsEvents.length >= DASHBOARD_RECENT_EVENT_LIMIT;
  }

  /**
   * Whether this user may read the readiness contract. The API gates
   * `capability-readiness` behind `requireAdminAccess`, so for anyone else the
   * request is a guaranteed 403 — it is never issued, and the readiness row and
   * card are omitted entirely rather than rendered empty.
   */
  get readinessEnabled(): boolean {
    return isAdmin(this.rootStore);
  }

  /** Worst-first capability rows (unready before ready, critical before info). */
  get capabilities(): CapabilityRecord[] {
    return sortCapabilities(this.readiness?.capabilities ?? []);
  }

  /** Server rollup when present, recomputed from the rows when it is not. */
  get readinessSummary(): CapabilityReadinessSummary {
    return summarizeCapabilities(this.readiness);
  }

  /**
   * not-ready OR unknown at `critical` severity — the "act now" count that
   * heads the attention band. Forced to 0 for non-admins, who have no readiness
   * data and must not be shown a phantom row.
   */
  get criticalUnready(): number {
    return this.readinessEnabled ? this.readinessSummary.criticalUnready : 0;
  }

  /**
   * Sections the API reported it could not read on the last good poll
   * (`summary.unavailable`). Their blocks carry zeros for old readers; those
   * zeros are NOT counts, and every card reads this before it prints one. An
   * API older than the field omits it, which reads as "all available".
   */
  get unavailableSections(): ReadonlySet<DashboardSection> {
    return new Set(this.summary?.unavailable ?? []);
  }

  /** True when `section`'s numbers are a failed read, not data. */
  isSectionUnavailable(section: DashboardSection): boolean {
    return this.unavailableSections.has(section);
  }

  /**
   * The ranked attention list. Pure derivation — see
   * CommandCenter.attention.ts for the ordering contract.
   */
  get attentionSignals(): AttentionSignal[] {
    // The count is handed over ONLY while it is this person's. A response
    // discarded by loadTickets' current-user re-check still runs the `finally`
    // that sets `ticketsSettled`, so between an account switch and the new
    // person's first count the band would otherwise publish the PREVIOUS
    // account's "3 tickets are waiting on you" as theirs — and the previous
    // account's failure as their failure.
    const mine = this.ticketsAreCurrentUsers();
    return deriveAttentionSignals({
      summary: this.summary,
      criticalUnready: this.criticalUnready,
      ticketsWaitingOnMe: mine ? this.ticketsWaitingOnMe : null,
      ticketsCheckFailed: mine ? this.ticketsCheckFailed : false,
    });
  }

  /**
   * May the attention band render a VERDICT yet?
   *
   * An empty signal list means two very different things: "nothing is
   * outstanding" and "nothing is known yet". Rendering "All clear" from the
   * second is a confident lie on the first paint of the most important element
   * on the page, so the band renders nothing until the summary has arrived —
   * and, for an admin, until readiness has settled too (otherwise a critical
   * capability row pops in a beat after an all-clear).
   */
  get attentionReady(): boolean {
    if (!this.summary) return false;
    // And until the ticket count has settled FOR THIS PERSON. This gate is
    // only about the FIRST answer: a count that then FAILS still settles, and
    // is kept out of "All clear" by the `tickets-unchecked` row, not by
    // holding the band. But a count settled for the account that just left is
    // no answer at all — without the second half, the band would draw a
    // verdict for the new person out of the old person's number.
    if (!this.ticketsSettled || !this.ticketsAreCurrentUsers()) return false;
    return !this.readinessEnabled || this.readinessSettled;
  }

  // ---- Loading --------------------------------------------------------------

  /** Fetch the composite rollup. Failures keep the previous summary. */
  async load() {
    const seq = ++this.loadSeq;
    this.loading = true;
    try {
      // `dashboard-summary` find() resolves the whole composite object (no
      // `{ data }` envelope), so read the find() RESULT rather than entities.
      const service = this.summaryService;
      const result = (await service?.find({ query: {} })) as
        | unknown as DashboardSummary | null | undefined;
      if (seq !== this.loadSeq) return; // a newer load superseded this one

      // The library ServiceStore SWALLOWS a transport error — it records
      // `errorOnFind` and resolves the find to `null` rather than throwing — so
      // a null result here means the fetch FAILED, not "no data". Gate success
      // on a truthy result (this rollup always returns a composite object on
      // success): otherwise a backend 500 would silently blank every card with
      // no error message and no Retry button. See CommandCenter.Body.tsx.
      if (!result) {
        // Not a failure if a sign-out ended the call. reset() bumps loadSeq, so
        // the check above already returns for that; this keeps the branch
        // right without leaning on it.
        if (swallowedSessionEnded(service)) return;
        const findError = (service as { errorOnFind?: unknown } | null)
          ?.errorOnFind;
        runInAction(() => {
          // Keep the previous summary (if any) so the Body shows the stale-data
          // warning; with none it shows the "Dashboard unavailable" retry state.
          this.error =
            findError instanceof Error
              ? findError.message
              : findError
                ? String(findError)
                : "Failed to load dashboard";
        });
        console.warn("dashboard-summary load failed (swallowed by ServiceStore)");
        return;
      }

      runInAction(() => {
        this.summary = result;
        // The server feed is authoritative again — drop the live overlay.
        this.liveEvents = [];
        this.error = null;
        this.lastUpdatedAt = new Date();
      });
    } catch (err: unknown) {
      if (seq !== this.loadSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to load dashboard";
        }
      });

      console.warn("dashboard-summary load failed:", err);
    } finally {
      if (seq === this.loadSeq) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /**
   * Fetch the admin-only capability readiness report.
   *
   * NEVER issues a request for a non-admin — the API gates the service behind
   * `requireAdminAccess`, so a viewer or operator would get a 403 on every 60s
   * tick and a permanent error card for a panel they cannot use.
   *
   * Failure handling mirrors `load()` exactly, and for the same reason: the
   * library ServiceStore swallows a transport error into `errorOnFind` and
   * RESOLVES `find()` to null, so success is gated on a truthy RESULT and the
   * message is read off `errorOnFind`. A failure keeps the last good report
   * (the card shows the stale rows) and never touches `summary` or `error`.
   */
  async loadReadiness() {
    if (!this.readinessEnabled) return;
    const seq = ++this.readinessSeq;
    this.readinessLoading = true;
    try {
      const service = this.readinessService;
      const result = (await service?.find({ query: {} })) as
        | unknown as CapabilityReadiness | null | undefined;
      if (seq !== this.readinessSeq) return; // superseded

      if (!result) {
        // As in load(): a sign-out is not a failed report.
        if (swallowedSessionEnded(service)) return;
        const findError = (service as { errorOnFind?: unknown } | null)
          ?.errorOnFind;
        runInAction(() => {
          this.readinessError =
            findError instanceof Error
              ? findError.message
              : findError
                ? String(findError)
                : "Failed to load capability readiness";
        });
        console.warn(
          "capability-readiness load failed (swallowed by ServiceStore)"
        );
        return;
      }

      runInAction(() => {
        this.readiness = result;
        this.readinessError = null;
      });
    } catch (err: unknown) {
      if (seq !== this.readinessSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.readinessError =
            err instanceof Error
              ? err.message
              : "Failed to load capability readiness";
        }
      });
      console.warn("capability-readiness load failed:", err);
    } finally {
      if (seq === this.readinessSeq) {
        runInAction(() => {
          this.readinessLoading = false;
          // Settled = an answer exists, good or bad. The attention band waits
          // on this so it never prints "All clear" before readiness replies.
          this.readinessSettled = true;
        });
      }
    }
  }

  // ---- Realtime -------------------------------------------------------------

  /**
   * Prepend a live `devops-event` to the activity feed and bump the 24h
   * counter. The row gets a synthetic id (the persisted uuid is not on the
   * wire envelope); the next successful poll replaces it with the real row.
   */
  handleLiveEvent(envelope: HomeDevopsEnvelope) {
    runInAction(() => {
      const type = typeof envelope.type === "string" ? envelope.type : "";
      const row: DashboardDevopsEventRow = {
        id: `live-${++this.liveSeq}`,
        type: type || "devops.event",
        title: buildDevOpsEventTitle(type, envelope.summary),
        clientId: envelope.clientId ?? null,
        occurredAt: envelope.at ?? new Date().toISOString(),
      };
      this.liveEvents = [row, ...this.liveEvents].slice(0, 10);
      if (this.summary?.devopsEvents) {
        this.summary.devopsEvents.last24h += 1;
      }
    });
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.summary = null;
    this.ticketsWaitingOnMe = null;
    this.loading = false;
    this.error = null;
    this.lastUpdatedAt = null;
    this.liveEvents = [];
    this.readiness = null;
    this.readinessLoading = false;
    this.readinessError = null;
    this.readinessSettled = false;
    this.refreshTimer = null;
    this.unsubscribeLive = null;
    this.loadSeq += 1;
    this.readinessSeq += 1;
    this.liveSeq += 1;
    this.ticketsSettled = false;
    this.ticketsCheckFailed = false;
    this.ticketsCountedFor = undefined;
  }
}

export default CommandCenterStore;
